// src/services/visitService.js
//
// Frontend API wrapper for "visits" domain.
//
// Goals
// -----
// 1) Keep network calls in one place.
// 2) Use clear, domain-friendly function names.
// 3) Avoid noisy console logging (especially for success paths).
// 4) Provide consistent error handling and messages.
// 5) Keep request contracts documented close to the API surface.
//
// Logging policy (recommended)
// ----------------------------
// - Service layer should NOT spam console.log on success.
// - Service layer may log errors ONLY when a debug flag is enabled.
// - UI layer is responsible for user-facing messages and UX.
//
// Enable debug logs locally:
//   REACT_APP_DEBUG_API=true
//

import apiClient from "../utils/apiClient";
import { idempotentPut } from "../utils/idempotentStockPosting";

// Toggle error logging without changing code.
// In CRA / Vite, env values are strings.
const DEBUG_API = String(process.env.REACT_APP_DEBUG_API || "")
  .toLowerCase()
  .trim() === "true";

/**
 * Internal: extract a safe, readable message from Axios errors and backend bodies.
 * We keep this lightweight and consistent across service methods.
 */
function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

/**
 * Internal: build and throw a normalized Error that callers can handle.
 * Adds useful metadata without leaking too much.
 */
function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  // Attach diagnostics for programmatic handling (optional).
  e.status = err?.response?.status;
  e.data = err?.response?.data || err?.data;
  e.outcomeUnknown = Boolean(err?.outcomeUnknown || e.data?.outcome_unknown);

  // Debug log only when explicitly enabled.
  if (DEBUG_API) {
    // Keep it concise—don’t dump full objects unless you really need to.
    // If you need more, log err.response in the UI debugging session.
    // eslint-disable-next-line no-console
    console.error(`[VisitService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Fetch visits based on selected filters.
 *
 * Backend:
 *   GET /visits/filter?from=YYYY-MM-DD&to=YYYY-MM-DD&userIds=<id>&userIds=<id>...
 *
 * Notes:
 * - We pass userIds as repeated query parameters (userIds=1&userIds=2),
 *   because many backends (Express/Sequelize) parse this naturally.
 * - We keep withCredentials:true because your app uses cookie/session auth.
 *
 * Return:
 * - Prefer backend contract: response.data.data = [ ...visits ]
 * - Defensive fallback: response.data = [ ...visits ] (if backend returns array directly)
 */
export const fetchVisits = async (from, to, userIds = []) => {
  try {
    // Basic input validation helps avoid hard-to-debug server calls.
    if (!from || !to) {
      const e = new Error("fetchVisits: 'from' and 'to' are required (YYYY-MM-DD).");
      e.code = "VISITS_DATE_RANGE_MISSING";
      throw e;
    }

    const params = new URLSearchParams();
    params.set("from", String(from));
    params.set("to", String(to));

    // Add userIds only if present.
    if (Array.isArray(userIds) && userIds.length > 0) {
      userIds
        .filter((id) => id !== undefined && id !== null && `${id}`.trim() !== "")
        .forEach((id) => params.append("userIds", String(id)));
    }

    const res = await apiClient.get(`/visits/filter?${params.toString()}`, {
      withCredentials: true,
    });

    // Defensive extraction: support both { data: { data: [] } } and [].
    const payload = res?.data;
    const visits = payload?.data ?? payload;

    if (!Array.isArray(visits)) {
      // If backend returns something unexpected, fail loudly (helps debugging).
      const e = new Error("fetchVisits: unexpected response shape (expected array).");
      e.code = "VISITS_BAD_RESPONSE";
      e.data = payload;
      throw e;
    }

    return visits;
  } catch (err) {
    throwApiError("fetchVisits", err);
  }
};

/**
 * Updates a specific field in the visit record.
 *
 * Backend:
 *   PUT /visits/:id
 *   Body: { [field]: value }
 *
 * Example:
 *   updateVisitField(123, "status", "confirmed")
 *
 * Notes:
 * - This endpoint pattern is flexible but easy to misuse.
 *   Consider restricting 'field' names at the UI layer if needed.
 */
export const updateVisitField = async (visitId, field, value) => {
  try {
    if (visitId === undefined || visitId === null || `${visitId}`.trim() === "") {
      const e = new Error("updateVisitField: visitId is required.");
      e.code = "VISIT_ID_MISSING";
      throw e;
    }

    if (!field || `${field}`.trim() === "") {
      const e = new Error("updateVisitField: field is required.");
      e.code = "VISIT_FIELD_MISSING";
      throw e;
    }

    const put = field === "checked" ? (url, body, config) => idempotentPut(apiClient, url, body, config) : apiClient.put.bind(apiClient);
    const res = await put(
      `/visits/${visitId}`,
      { [field]: value },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    // Axios treats non-2xx as errors by default, but keep defensive checks.
    if (res.status < 200 || res.status >= 300) {
      const e = new Error(`updateVisitField: HTTP ${res.status}`);
      e.status = res.status;
      e.data = res.data;
      throw e;
    }

    return res.data;
  } catch (err) {
    throwApiError("updateVisitField", err);
  }
};

/**
 * Delete a visit entry.
 *
 * Backend:
 *   DELETE /visits/:id
 *
 * Return:
 * - returns backend response body (if any) for UI to decide what to do,
 *   otherwise returns { ok: true } as a fallback.
 */
export const deleteVisit = async (visitId) => {
  try {
    if (visitId === undefined || visitId === null || `${visitId}`.trim() === "") {
      const e = new Error("deleteVisit: visitId is required.");
      e.code = "VISIT_ID_MISSING";
      throw e;
    }

    const res = await apiClient.delete(`/visits/${visitId}`, {
      withCredentials: true,
    });

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteVisit", err);
  }
};

/**
 * Upload a file (Excel) to the backend.
 *
 * Backend:
 *   POST /visits/upload
 *   multipart/form-data with field name 'file'
 *
 * Return:
 * - response.data.message (if provided) or a generic success message.
 *
 * Notes:
 * - Do not log filenames or paths by default (privacy + noise).
 */
export const uploadFile = async (file) => {
  try {
    if (!file) {
      const e = new Error("uploadFile: file is required.");
      e.code = "UPLOAD_FILE_MISSING";
      throw e;
    }

    const formData = new FormData();
    formData.append("file", file);

    const res = await apiClient.post("/visits/upload", formData, {
      withCredentials: true,
      // IMPORTANT: When sending FormData, do not manually set boundary.
      // Axios will set a proper multipart boundary automatically.
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000, // allow time for larger files if needed
    });

    return res?.data?.message || "File uploaded successfully.";
  } catch (err) {
    throwApiError("uploadFile", err);
  }
};

/**
 * List files in /srv/hb_visits/backups/excel (name/size/modified).
 *
 * Backend:
 *   GET /visits/files
 *
 * Return:
 *   [{ name, size, modified }, ...]
 */
export const listExcelFiles = async () => {
  try {
    const res = await apiClient.get("/visits/files", {
      withCredentials: true,
      timeout: 20000,
    });

    const files = res?.data?.files;
    return Array.isArray(files) ? files : [];
  } catch (err) {
    throwApiError("listExcelFiles", err);
  }
};

/**
 * Trigger the full Booksy flow:
 *  - fetch from email
 *  - import latest
 *
 * Backend:
 *   POST /visits/run-booksy-flow
 *
 * Return (expected):
 *   {
 *     ok: true,
 *     fetch: {...},
 *     import: {...}
 *   }
 */
export const runBooksyFlow = async () => {
  try {
    const res = await apiClient.post(
      "/visits/run-booksy-flow",
      {},
      {
        withCredentials: true,
        timeout: 300000,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("runBooksyFlow", err);
  }
};

/**
 * Optional default export (consistent with other services if you prefer).
 */
export default {
  fetchVisits,
  updateVisitField,
  deleteVisit,
  uploadFile,
  listExcelFiles,
  runBooksyFlow,
};


